import { useEffect, useMemo } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { useAtom } from 'jotai';

import { useImageFallback } from '@/utils/hooks';

import type {
  GetPatientLatestImagesResponse,
  LatestImage,
} from '../../@types/latestImages';
import type { ImageAnalyses } from '../../@types/latestImagesV2';
import VideaLogoSm from '../../common-components/elements/icon/svgs/VideaBrandSm';
import { env } from '../../config';
import { useSaveAttachmentAudit } from '../../react-query/rcm/useSaveAttachmentAudit';
import { analysesToAnalyzedImages } from '../../react-query/useLatestImagesv2';
import { usePreferences } from '../../react-query/usePreferences';
import { useCornerstoneInitialization } from '../../utils/cornerstone/cornerstoneAtoms';
import { useUniversalToFDI } from '../../utils/internationalization/useUniversalToFDI';
import { ImageThumbnail } from '../claims/images/ImageThumbnail';
import { useFormatSelectedDistinctTeeth } from '../odontogramV2/helpers';
import { selectTeethAtom } from '../patientList/atoms';

import type { InsuranceView } from './constants';
import {
  PAGINATION_IMAGE_COUNT,
  useSummaryBackgroundContent,
} from './constants';
import { SimpleThumbnail } from './SimpleThumbnail';
import {
  FindingTable,
  ImageSection,
  ImageSet,
  ImageTitle,
  SummaryContent,
  SummaryPage,
} from './styles';
import {
  CariesFindings,
  EndoFindingImages,
  RBLFindingImages,
} from './subcomponents';

export type TTreatmentDisplay = { cdtCode?: string; quadrant?: string };

const ImageForJpg = ({
  filteredImages,
  currentPage,
  hasPagination,
  imageFindingsResto,
  imageFindingsPerio,
  imageFindingsEndo,
  imageFindingsCalculus,
  imageCount,
}: {
  filteredImages?: LatestImage[];
  currentPage: number;
  hasPagination?: boolean;
  imageFindingsResto: boolean;
  imageFindingsPerio: boolean;
  imageFindingsEndo: boolean;
  imageFindingsCalculus: boolean;
  imageCount: number;
}) => {
  if (imageCount === 0) return null;
  const findingTypes: string[] | undefined = [];

  if (imageFindingsResto) {
    findingTypes.push('caries');
  }
  if (imageFindingsPerio) {
    findingTypes.push('radiographic_boneloss');
  }
  if (imageFindingsEndo) {
    findingTypes.push('endodontic');
  }
  if (imageFindingsCalculus) {
    findingTypes.push('radiographic_boneloss_calculus');
  }

  return (
    <div className="jpg-wrapper">
      {filteredImages?.map((image, index) => (
        <div key={`ins-image-${image.imageId}`}>
          <ImageTitle>
            <Trans>Image</Trans> {hasPagination && `${currentPage + 1}.`}
            {index + 1}
          </ImageTitle>
          <ImageSection
            className="thumbnail-set-section"
            key={`image-section-${image.imageId}`}
            
            data-testid="claim-summary-image-section"
          >
            <div className="thumbnail-image-section">
              <div className="thumbnail-group">
                <div className="thumbnail-group-title">
                  <Trans>ORIGINAL</Trans>
                </div>
                <SimpleThumbnail
                  key={`plain-${image.imageId}`}
                  image={image}
                  hideAnnotations={true}
                  hasPagination={hasPagination}
                />
              </div>
              <div className="thumbnail-group">
                <div className="thumbnail-group-title">
                  <Trans>ANNOTATED</Trans>
                </div>
                <SimpleThumbnail
                  key={`anno-${image.imageId}`}
                  image={image}
                  hideAnnotations={false}
                  findingTypes={findingTypes}
                  hasPagination={hasPagination}
                />
              </div>
            </div>
            <div className="thumbnail-analysis-section">
              {imageFindingsResto && <CariesFindings image={image} />}
              {imageFindingsPerio && <RBLFindingImages image={image} />}
              {imageFindingsEndo && <EndoFindingImages image={image} />}
              {!imageFindingsResto &&
                !imageFindingsPerio &&
                !imageFindingsEndo && (
                  <FindingTable className="finding-table">
                    <thead>
                      <tr>
                        <th colSpan={2} className="finding-head">
                          <Trans>
                            Tooth numbers not detected for this image, so
                            finding information cannot be added.
                          </Trans>
                        </th>
                      </tr>
                    </thead>
                  </FindingTable>
                )}
            </div>
          </ImageSection>
        </div>
      ))}
    </div>
  );
};

const ImageForJpgV2 = ({
  filteredImages,
  currentPage,
  hasPagination,
  imageFindingsResto,
  imageFindingsPerio,
  imageFindingsEndo,
  imageFindingsCalculus,
  imageCount,
}: {
  filteredImages?: ImageAnalyses[];
  currentPage: number;
  hasPagination?: boolean;
  imageFindingsResto: boolean;
  imageFindingsPerio: boolean;
  imageFindingsEndo: boolean;
  imageFindingsCalculus: boolean;
  imageCount: number;
}) => {
  if (imageCount === 0) return null;
  const findingTypes: string[] | undefined = [];

  if (imageFindingsResto) {
    findingTypes.push('caries');
  }
  if (imageFindingsPerio) {
    findingTypes.push('radiographic_boneloss');
  }
  if (imageFindingsEndo) {
    findingTypes.push('endodontic');
  }
  if (imageFindingsCalculus) {
    findingTypes.push('radiographic_boneloss_calculus');
  }

  const legacyImages = analysesToAnalyzedImages(filteredImages ?? []);

  return (
    <div className="jpg-wrapper">
      {filteredImages?.map((image, index) => {
        const legacyImage = legacyImages.find(
          (i) => i.imageManifestId === image.imageId,
        ) as LatestImage;
        return (
          <div key={`ins-image-${image.imageId}`}>
            <ImageTitle>
              <Trans>Image</Trans> {hasPagination && `${currentPage + 1}.`}
              {index + 1}
            </ImageTitle>
            <ImageSection
              className="thumbnail-set-section"
              key={`image-section-${image.imageId}`}
              
              data-testid="claim-summary-image-section"
            >
              <div className="thumbnail-image-section">
                <div className="thumbnail-group">
                  <div className="thumbnail-group-title">
                    <Trans>ORIGINAL</Trans>
                  </div>
                  <ImageThumbnail
                    height={250}
                    width={250}
                    image={image}
                    enabledFindings={[]}
                  />
                </div>
                <div className="thumbnail-group">
                  <div className="thumbnail-group-title">
                    <Trans>ANNOTATED</Trans>
                  </div>
                  <ImageThumbnail
                    height={250}
                    width={250}
                    image={image}
                    enabledFindings={findingTypes}
                  />
                </div>
              </div>
              <div className="thumbnail-analysis-section">
                {imageFindingsResto && <CariesFindings image={legacyImage} />}
                {imageFindingsPerio && <RBLFindingImages image={legacyImage} />}
                {imageFindingsEndo && <EndoFindingImages image={legacyImage} />}
                {!imageFindingsResto &&
                  !imageFindingsPerio &&
                  !imageFindingsEndo && (
                    <FindingTable className="finding-table">
                      <thead>
                        <tr>
                          <th colSpan={2} className="finding-head">
                            <Trans>
                              Tooth numbers not detected for this image, so
                              finding information cannot be added.
                            </Trans>
                          </th>
                        </tr>
                      </thead>
                    </FindingTable>
                  )}
              </div>
            </ImageSection>
          </div>
        );
      })}
    </div>
  );
};

const DmfTable = ({
  dmfValues,
  imageFindingsResto,
  imageFindingsDmf,
}: {
  dmfValues: { toothNumber: string; dmf: string }[];
  imageFindingsResto: boolean;
  imageFindingsDmf: boolean;
}) => {
  const { displayToothNumber } = useUniversalToFDI();

  const showDmfFindings = !!dmfValues && imageFindingsDmf;

  return (
    <FindingTable
      className="finding-table"
      
      data-testid="claim-summary-dmf-table"
    >
      <thead>
        <tr>
          <th colSpan={2} className="finding-head">
            <Trans>DMF Findings</Trans>
          </th>
        </tr>
        <tr>
          <th className="finding-tooth-identifier">
            <Trans>Tooth</Trans>
          </th>
          <th className="finding-tooth-value">
            <Trans>% of Crown Tooth Structure Loss</Trans>
          </th>
        </tr>
      </thead>
      <tbody>
        {showDmfFindings ? (
          <>
            {dmfValues.map((t) => (
              <tr key={`dmf-${t.toothNumber}`}>
                <td>{displayToothNumber(t.toothNumber)}</td>
                <td>{t.dmf}</td>
              </tr>
            ))}
          </>
        ) : (
          <tr>
            <td colSpan={2}>
              <Trans>No DMF findings</Trans>
            </td>
          </tr>
        )}
      </tbody>
    </FindingTable>
  );
};

const Notes = ({
  selectedTreatments,
  isSrp,
  narrativeText,
  clinicalNotes,
}: {
  selectedTreatments: TTreatmentDisplay[];
  isSrp: boolean;
  narrativeText: string;
  clinicalNotes: string;
}) => {
  const narrativeEl = (
    <div className="thumbnail-narrative">
      <div className="thumbnail-narrative-title">
        <Trans>Narrative/Notes</Trans>
      </div>
      <div className="thumbnail-narrative-text">
        {narrativeText || 'None provided.'}
      </div>
      {isSrp && (
        <>
          <br />
          <div className="thumbnail-narrative-title">
            <Trans>Clinical Notes</Trans>
          </div>
          <div className="thumbnail-narrative-text">
            {clinicalNotes || 'None provided.'}
          </div>
          {selectedTreatments.length > 0 && (
            <>
              <br />
              <div className="thumbnail-narrative-title">
                <Trans>Treatments</Trans>
              </div>
              {selectedTreatments.map((treatment) => (
                <div className="thumbnail-narrative-text">
                  {`${treatment.cdtCode} (${
                    treatment.quadrant ?? 'Quadrant not specified'
                  })`}
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );

  return narrativeEl;
};

const ImageHeader = ({
  selectedDistinctTeeth,
  currentPage,
  radiographsTakenDate,
  patientName,
  dob,
  visitDate,
  includeRadiographsTakenDate,
}: {
  selectedDistinctTeeth: string;
  currentPage: number;
  radiographsTakenDate: string;
  patientName: string;
  dob: string;
  visitDate: string;
  includeRadiographsTakenDate: boolean;
}) => {
  return (
    <FindingTable className="finding-table" data-testid="claim-summary-header">
      <thead>
        <tr>
          <th rowSpan={2} className="image-logo-head">
            <VideaLogoSm />
          </th>
          <th className="image-head">
            <Trans>Patient Name</Trans>
          </th>
          <th className="image-head">
            <Trans>Patient Date of Birth</Trans>
          </th>
          <th className="image-head">
            <Trans>Patient Visit Date</Trans>
          </th>
          {includeRadiographsTakenDate && radiographsTakenDate && (
            <th className="image-head">
              <Trans>Radiographs Taken Date</Trans>
            </th>
          )}
          <th className="image-head">
            <Trans>Tooth numbers shown</Trans>
          </th>
          <th className="image-head page">
            <Trans>Page</Trans>
          </th>
        </tr>
        <tr>
          <td className="image-head-value">{patientName}</td>
          <td className="image-head-value">{dob}</td>
          <td className="image-head-value" >
            {visitDate}
          </td>
          {includeRadiographsTakenDate && radiographsTakenDate && (
            <td
              className="image-head-value"
              
            >
              {radiographsTakenDate}
            </td>
          )}
          <td className="image-head-value teeth-numbers">
            {selectedDistinctTeeth}
          </td>
          <td className="image-head-value page">{currentPage + 1}</td>
        </tr>
      </thead>
    </FindingTable>
  );
};

const ImageAttachmentId = ({
  attachmentId,
}: {
  attachmentId: string | undefined;
}) => {
  if (env === 'production' || !attachmentId) {
    return <></>;
  }
  return (
    <p>
      <Trans>Attachment ID: {attachmentId}</Trans>
    </p>
  );
};

const CoverContent = () => {
  const { background, rbl, display } = useSummaryBackgroundContent();
  const {
    i18n: { locale },
  } = useLingui();

  const { imgSrc: rblImgSrc, onError: rblOnError } = useImageFallback(
    `/assets/images/rblMeasurementGuide/insdiagram-${locale}.png`,
    '/assets/images/rblMeasurementGuide/insdiagram-en.png',
  );

  return (
    <SummaryContent>
      <section>
        <h2>{background.title}</h2>
        <p>{background.body}</p>
      </section>
      <section>
        <h2>{rbl.title}</h2>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <img
          src={rblImgSrc}
          alt="RBL"
          className="rbl-diagram"
          onError={rblOnError}
        />

        <p>{rbl.body}</p>
      </section>
      <section>
        <h2>{display.title}</h2>
        <p>{display.body}</p>
      </section>
    </SummaryContent>
  );
};

const RenderImage = ({
  imageSetV2,
  hasPagination,
  currentPage,
  imageFindingsResto,
  imageFindingsPerio,
  imageFindingsEndo,
  imageSet,
}: {
  hasPagination: boolean | undefined;
  currentPage: number;
  imageFindingsResto: boolean;
  imageFindingsPerio: boolean;
  imageFindingsEndo: boolean;
  imageSet: LatestImage[];
  imageSetV2: ImageAnalyses[];
}) => {
  const prefs = usePreferences();

  if (prefs.data.newImageAnnotatorThumbnailsForClaims) {
    return (
      <ImageForJpgV2
        filteredImages={imageSetV2}
        hasPagination={hasPagination}
        currentPage={currentPage}
        imageFindingsResto={imageFindingsResto}
        imageFindingsPerio={imageFindingsPerio}
        imageFindingsEndo={imageFindingsEndo}
        imageFindingsCalculus={imageFindingsPerio}
        imageCount={imageSet?.length || 0}
      />
    );
  }

  return (
    <ImageForJpg
      filteredImages={imageSet}
      hasPagination={hasPagination}
      currentPage={currentPage}
      imageFindingsResto={imageFindingsResto}
      imageFindingsPerio={imageFindingsPerio}
      imageFindingsEndo={imageFindingsEndo}
      imageFindingsCalculus={imageFindingsPerio}
      imageCount={imageSet?.length || 0}
    />
  );
};

export const InsuranceImages = ({
  images,
  dmfValues,
  hasPagination,
  currentPage = 0,
  hidePMSInformation,
  estimatedVisitId,
  pmsAppointmentId,
  insPage,
  selectedTreatments,
  isSrp,
  imageFindingsResto,
  imageFindingsDmf,
  radiographsTakenDate,
  imageBw,
  narrativeText,
  imageFindingsPerio,
  imageFindingsEndo,
  imageSetGroup,
  clinicalNotes,
  patientName,
  dob,
  visitDate,
  includeRadiographsTakenDate,
  imagesV2,
}: {
  images: GetPatientLatestImagesResponse['analyzedImages'] | undefined;
  dmfValues?: { toothNumber: string; dmf: string }[] | undefined;
  hasPagination?: boolean;
  currentPage?: number;
  hidePMSInformation?: boolean;
  isPaginated?: boolean;
  estimatedVisitId?: number;
  pmsAppointmentId?: string;
  insPage: InsuranceView;
  selectedTreatments: TTreatmentDisplay[];
  isSrp: boolean;
  imageFindingsResto: boolean;
  imageFindingsDmf: boolean;
  radiographsTakenDate: string;
  imageBw: boolean;
  narrativeText: string;
  imageFindingsPerio: boolean;
  imageFindingsEndo: boolean;
  imageSetGroup: string;
  clinicalNotes: string;
  patientName: string;
  dob: string;
  visitDate: string;
  includeRadiographsTakenDate: boolean;
  imagesV2: ImageAnalyses[];
}) => {
  const [selectedTeeth] = useAtom(selectTeethAtom);
  const { data: prefs } = usePreferences();
  const showNarrative = prefs?.cleanClaims;

  const { mutate: saveAuditEntry, data } = useSaveAttachmentAudit();

  useEffect(() => {
    const imageIds = images?.map((i) => i.imageManifestId);
    saveAuditEntry({
      imageIds: imageIds ?? [],
      options: {
        includeCariesFindings: !!imageFindingsResto,
        includeAllImages: imageSetGroup === 'all',
        includeDmfFindings: !!imageFindingsDmf,
        bitewingsOnly: !!imageBw,
        includePrlFindings: !!imageFindingsEndo,
        includeRblAndCalculusFindings: !!imageFindingsPerio,
        selectedTeeth: selectedTeeth,
      },
      narrative: showNarrative ? narrativeText : undefined,
      estimatedVisitId,
      pmsAppointmentId,
      view: insPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useCornerstoneInitialization();

  const pageIndex = currentPage - 1;
  const imageSet = useMemo(() => {
    let currImages = hasPagination
      ? (images?.slice(
          pageIndex * PAGINATION_IMAGE_COUNT,
          (pageIndex + 1) * PAGINATION_IMAGE_COUNT,
        ) as LatestImage[])
      : (images as LatestImage[]);

    if (imageBw) {
      currImages = currImages?.filter(
        (image: any) => image.detectedImageType === 'BITEWING',
      );
    }

    return currImages;
  }, [hasPagination, imageBw, images, pageIndex]);

  const imageSetV2 = (imagesV2 ?? []).filter((img) =>
    imageSet.map((i) => i.imageManifestId).includes(img.imageId),
  );

  const displayTeeth = useFormatSelectedDistinctTeeth(imageSet || []);

  if (!hasPagination) {
    return (
      <SummaryPage>
        <ImageHeader
          selectedDistinctTeeth={displayTeeth}
          currentPage={currentPage}
          radiographsTakenDate={radiographsTakenDate}
          patientName={patientName}
          dob={dob}
          visitDate={visitDate}
          includeRadiographsTakenDate={includeRadiographsTakenDate}
        />

        <ImageAttachmentId attachmentId={data?.data?.attachmentId} />
        <div className="section">
          {!hidePMSInformation && showNarrative && (
            <Notes
              selectedTreatments={selectedTreatments}
              isSrp={isSrp}
              narrativeText={narrativeText}
              clinicalNotes={clinicalNotes}
            />
          )}
          <div className="columns">
            <CoverContent />
            <DmfTable
              dmfValues={dmfValues || []}
              imageFindingsResto={imageFindingsResto}
              imageFindingsDmf={imageFindingsDmf}
            />
          </div>
        </div>
        <ImageSet className="thumbnail-set">
          <RenderImage
            imageSet={imageSet}
            imageSetV2={imageSetV2}
            hasPagination={hasPagination}
            currentPage={currentPage}
            imageFindingsResto={imageFindingsResto}
            imageFindingsPerio={imageFindingsPerio}
            imageFindingsEndo={imageFindingsEndo}
          />
        </ImageSet>
      </SummaryPage>
    );
  }
  if (hasPagination && currentPage === 0) {
    return (
      <SummaryPage style={{ minHeight: '88em' }}>
        <ImageHeader
          selectedDistinctTeeth={displayTeeth}
          currentPage={currentPage}
          radiographsTakenDate={radiographsTakenDate}
          patientName={patientName}
          dob={dob}
          visitDate={visitDate}
          includeRadiographsTakenDate={includeRadiographsTakenDate}
        />

        <ImageAttachmentId attachmentId={data?.data?.attachmentId} />
        {showNarrative && (
          <Notes
            selectedTreatments={selectedTreatments}
            isSrp={isSrp}
            narrativeText={narrativeText}
            clinicalNotes={clinicalNotes}
          />
        )}
        <div className="columns">
          <CoverContent />
          <DmfTable
            dmfValues={dmfValues || []}
            imageFindingsResto={imageFindingsResto}
            imageFindingsDmf={imageFindingsDmf}
          />
        </div>
      </SummaryPage>
    );
  } else {
    return (
      <SummaryPage>
        <ImageHeader
          selectedDistinctTeeth={displayTeeth}
          currentPage={currentPage}
          radiographsTakenDate={radiographsTakenDate}
          patientName={patientName}
          dob={dob}
          visitDate={visitDate}
          includeRadiographsTakenDate={includeRadiographsTakenDate}
        />

        <ImageAttachmentId attachmentId={data?.data?.attachmentId} />
        <ImageSet className="thumbnail-set" >
          <RenderImage
            imageSet={imageSet}
            imageSetV2={imageSetV2}
            hasPagination={hasPagination}
            currentPage={currentPage}
            imageFindingsResto={imageFindingsResto}
            imageFindingsPerio={imageFindingsPerio}
            imageFindingsEndo={imageFindingsEndo}
          />
        </ImageSet>
      </SummaryPage>
    );
  }
};
